import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: '',
    caffeine_level: '',
    tea_type: ''
  });

  useEffect(() => {
    fetchProducts();
  }, [filters]);

  const fetchProducts = async () => {
    try {
      const params = {};
      if (filters.category) params.category = filters.category;
      if (filters.caffeine_level) params.caffeine_level = filters.caffeine_level;
      if (filters.tea_type) params.tea_type = filters.tea_type;
      
      const response = await axios.get(`${BACKEND_URL}/api/products`, { params });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen section-padding bg-soft-ivory" data-testid="shop-page">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16 space-y-4"
        >
          <h1 className="heading-1">Shop All Rituals</h1>
          <p className="body-text text-dark-cedar/70">Discover your perfect tea blend</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1 space-y-6">
            <div className="card-flat p-6 space-y-6">
              <h3 className="font-inter font-semibold text-dark-cedar">Filters</h3>
              
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={filters.category || "all"} onValueChange={(value) => setFilters({...filters, category: value === "all" ? "" : value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="Sleep">Sleep</SelectItem>
                    <SelectItem value="Desire">Desire</SelectItem>
                    <SelectItem value="Focus">Focus</SelectItem>
                    <SelectItem value="Calm">Calm</SelectItem>
                    <SelectItem value="Gut">Gut</SelectItem>
                    <SelectItem value="Breath">Breath</SelectItem>
                    <SelectItem value="Skin">Skin</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Caffeine Level</Label>
                <Select value={filters.caffeine_level || "all"} onValueChange={(value) => setFilters({...filters, caffeine_level: value === "all" ? "" : value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any</SelectItem>
                    <SelectItem value="None">Caffeine-free</SelectItem>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Tea Type</Label>
                <Select value={filters.tea_type || "all"} onValueChange={(value) => setFilters({...filters, tea_type: value === "all" ? "" : value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any</SelectItem>
                    <SelectItem value="Herbal">Herbal</SelectItem>
                    <SelectItem value="Green">Green</SelectItem>
                    <SelectItem value="Black">Black</SelectItem>
                    <SelectItem value="Oolong">Oolong</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            {loading ? (
              <div className="text-center py-20">
                <p className="text-dark-cedar/60">Loading products...</p>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-dark-cedar/60">No products found</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {products.map((product, index) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <ProductCard product={product} />
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}